<template>
  <div class="creation-form">
    <section class="cm-dialog" @click="closeOptionDropdown">
      <div class="cm-body">
        <div class="cm-field">
          <label class="cm-label" for="youtube-import-url">{{ $t('youtubeImporter.urlLabel') }}</label>
          <div class="cm-inline">
            <input
              id="youtube-import-url"
              v-model.trim="url"
              class="cm-input"
              type="url"
              inputmode="url"
              autocomplete="url"
              :placeholder="$t('youtubeImporter.urlPlaceholder')"
              :disabled="isInspecting || isImporting"
              @input="resetInspection"
              @keyup.enter="inspectVideo"
            >
            <button
              type="button"
              class="cm-icon-button"
              :title="$t('common.paste')"
              :aria-label="$t('common.paste')"
              :disabled="isInspecting || isImporting"
              @click="pasteUrl"
            >
              <span class="cm-icon i-paste" aria-hidden="true"></span>
            </button>
            <button
              type="button"
              class="cm-button"
              :disabled="!url || isInspecting || isImporting"
              @click="inspectVideo"
            >
              <span v-if="isInspecting" class="cm-spinner" aria-hidden="true"></span>
              {{ isInspecting ? $t('youtubeImporter.inspecting') : $t('youtubeImporter.inspect') }}
            </button>
          </div>
        </div>

        <PlanLimitNotice v-if="limit" kind="video" :minutes="limit.minutes" :limit-minutes="limit.limitMinutes" />
        <p v-else-if="error" class="cm-error" role="alert">{{ error }}</p>

        <div v-if="video" class="cm-media">
          <img :src="video.thumbnailUrl" :alt="video.title">
          <div>
            <h3 class="cm-media-title">{{ video.title }}</h3>
            <p class="cm-media-channel">{{ video.channel }}</p>
            <dl class="cm-facts">
              <div>
                <dt>{{ $t('youtubeImporter.duration') }}</dt>
                <dd>{{ formattedDuration }}</dd>
              </div>
              <div>
                <dt>{{ $t('youtubeImporter.planLimit') }}</dt>
                <dd>{{ $t('youtubeImporter.minutes', { count: video.limitMinutes }) }}</dd>
              </div>
            </dl>
          </div>
        </div>

        <div v-if="video" class="cm-group">
          <div class="cm-field">
            <span class="cm-label">{{ $t('audioUploader.providerLabel') }}</span>
            <div class="cm-segmented" role="radiogroup" :aria-label="$t('audioUploader.providerLabel')">
              <button
                v-for="provider in transcriptionProviders"
                :key="provider.value"
                type="button"
                role="radio"
                :class="{ active: provider.value === selectedProvider }"
                :aria-checked="provider.value === selectedProvider"
                :disabled="isImporting"
                @click="selectProvider(provider.value)"
              >
                {{ $t('audioUploader.providers.' + provider.labelKey) }}
              </button>
            </div>
          </div>
          <div class="cm-field">
            <label class="cm-label" for="youtube-language">{{ $t('audioUploader.languageLabel') }}</label>
            <div :class="['cm-select', { open: openOptionDropdown === 'language' }]" @click.stop>
              <button
                id="youtube-language"
                type="button"
                class="cm-select-button"
                :disabled="isImporting"
                :aria-expanded="openOptionDropdown === 'language'"
                aria-haspopup="listbox"
                @click="toggleOptionDropdown('language')"
              >
                <span class="cm-select-value">{{ selectedLanguageLabel }}</span>
                <span class="cm-select-arrow" aria-hidden="true"></span>
              </button>
              <div v-if="openOptionDropdown === 'language'" class="cm-select-menu is-scroll" role="listbox">
                <button
                  v-for="language in availableLanguages"
                  :key="language.value"
                  type="button"
                  :class="['cm-select-option', { selected: language.value === selectedLanguage }]"
                  role="option"
                  :aria-selected="language.value === selectedLanguage"
                  @click="selectLanguage(language.value)"
                >
                  <span>{{ $t('audioUploader.languages.' + language.labelKey) }}</span>
                  <span class="cm-select-check" aria-hidden="true"></span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <footer class="cm-footer">
        <span :class="['cm-footer-meta', { 'is-insufficient': video && !hasEnoughUsage }]">
          <template v-if="video && !hasEnoughUsage">
            {{ $t('youtubeImporter.insufficient', { count: video.usageCost, balance: userDetails?.remainingUsage || 0 }) }}
          </template>
          <template v-else-if="video">
            {{ $t('youtubeImporter.usage') }} <strong>{{ $t('youtubeImporter.credits', { count: video.usageCost }) }}</strong>
          </template>
        </span>
        <button
          type="button"
          class="cm-button-primary"
          :disabled="!video || isImporting"
          @click="importVideo"
        >
          <span v-if="isImporting" class="cm-spinner" aria-hidden="true"></span>
          {{ isImporting ? $t('youtubeImporter.preparing') : $t('youtubeImporter.create') }}
        </button>
      </footer>
    </section>
  </div>
</template>

<script>
import { useModals } from '@/composables/modals'
import { auth } from '@/firebase'
import { apiPost } from '@/composables/useApi'
import {
  getAvailableTranscriptionLanguages,
  getDefaultTranscriptionLanguage,
  transcriptionProviders,
} from '@/utils/transcriptionOptions'
import { formatDuration } from '@/utils/format'
import PlanLimitNotice from './PlanLimitNotice.vue'

export default {
  name: 'YouTubeImporter',
  components: { PlanLimitNotice },
  props: {
    userDetails: { type: Object, default: () => ({}) },
  },
  emits: ['file-generated', 'error'],
  data() {
    return {
      url: '',
      video: null,
      error: '',
      // 视频超出当前方案的时长上限时显示升级提示
      limit: null,
      isInspecting: false,
      isImporting: false,
      selectedProvider: 'deepgram',
      selectedLanguage: 'en',
      openOptionDropdown: null,
      transcriptionProviders,
    }
  },
  computed: {
    availableLanguages() {
      return getAvailableTranscriptionLanguages(this.selectedProvider)
    },
    selectedLanguageLabel() {
      const language = this.availableLanguages.find((item) => item.value === this.selectedLanguage)
      return language ? this.$t('audioUploader.languages.' + language.labelKey) : this.selectedLanguage
    },
    formattedDuration() {
      return formatDuration(this.video?.duration || 0)
    },
    hasEnoughUsage() {
      if (!this.video) return false
      return Number(this.userDetails?.remainingUsage || 0) >= Number(this.video.usageCost || 0)
    },
  },
  watch: {
    selectedProvider(provider) {
      this.selectedLanguage = getDefaultTranscriptionLanguage(provider)
    },
  },
  methods: {
    resetInspection() {
      this.video = null
      this.error = ''
      this.limit = null
    },
    closeOptionDropdown() {
      this.openOptionDropdown = null
    },
    toggleOptionDropdown(name) {
      this.openOptionDropdown = this.openOptionDropdown === name ? null : name
    },
    selectProvider(provider) {
      this.selectedProvider = provider
      this.closeOptionDropdown()
    },
    selectLanguage(language) {
      this.selectedLanguage = language
      this.closeOptionDropdown()
    },
    async pasteUrl() {
      if (this.isInspecting || this.isImporting) return
      try {
        const clipboardText = await navigator.clipboard.readText()
        const nextUrl = String(clipboardText || '').trim()
        if (!nextUrl) return
        this.url = nextUrl
        this.resetInspection()
      } catch {
        this.error = this.$t('youtubeImporter.pasteFailed')
      }
    },
    async inspectVideo() {
      if (!this.url || this.isInspecting) return
      if (!auth.currentUser) { useModals().showLoginModal(); return }
      this.isInspecting = true
      this.error = ''
      this.limit = null
      this.video = null
      try {
        this.video = await apiPost('/api/youtube/inspect', { url: this.url })
      } catch (error) {
        if (error.code === 'UPLOAD_DURATION_LIMIT_EXCEEDED') {
          this.limit = {
            minutes: Math.ceil(error.details.durationSeconds / 60),
            limitMinutes: error.details.limitMinutes,
          }
        } else {
          this.error = error.message
        }
      } finally {
        this.isInspecting = false
      }
    },
    async importVideo() {
      if (!this.video || this.isImporting) return
      if (!auth.currentUser) { useModals().showLoginModal(); return }
      if (!this.hasEnoughUsage) { useModals().handleUpgrade(); return }
      this.isImporting = true
      this.error = ''
      try {
        await apiPost('/api/youtube/import', {
          url: this.url,
          transcriptionProvider: this.selectedProvider,
          transcriptionLanguage: this.selectedLanguage,
        })
        this.$emit('file-generated')
      } catch (error) {
        this.error = error.message
        this.$emit('error', error.message)
      } finally {
        this.isImporting = false
      }
    },
  },
}
</script>
